/**
 * Ngữ cảnh xác thực — nạp `/auth/me` một lần rồi chia sẻ cho toàn ứng dụng.
 * Capability đọc lại từ server mỗi lần `refresh()` nên đổi vai trò có hiệu lực
 * ngay (docs/05 §5.7.5).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError, SU_KIEN_PHIEN_HET, api, ensureCsrfCookie } from './api';
import type { CurrentUser, MeResponse } from './types';

interface AuthValue {
  user: CurrentUser | null;
  capabilities: string[];
  /* Tên hệ thống và dòng phụ do Admin đặt; 
ull = chưa đặt, dùng bản dịch. */
  branding: { appName: string | null; tagline: string | null };
  mustSetupTwoFactor: boolean;
  /** Quản trị viên vừa đặt mật khẩu hộ — buộc đổi ngay (OQ-218). */
  mustChangePassword: boolean;
  loading: boolean;
  can: (capability: string) => boolean;
  refresh: () => Promise<void>;
  setSession: (me: MeResponse) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

/**
 * FR-114 · "màu nhấn" — áp giá trị Admin đặt vào biến CSS `--accent`.
 *
 * Trước M7 khoá `branding.accent_color` đi được nửa đường: `GET /auth/me` có
 * trả về (trường `theme`) nhưng SPA bỏ qua, nên Admin đổi màu và không thấy gì
 * — đúng dạng "tính năng giả" mà `docs/11 §11.6` cấm. Xem OQ-093.
 *
 * Chỉ nhận giá trị trình duyệt hiểu là MÀU: chuỗi lạ sẽ bị bỏ qua thay vì ghi
 * thẳng vào biến CSS. `CSS.supports` không có trong jsdom nên phải kiểm trước.
 */
function apDungMauNhan(value: string | null | undefined): void {
  const root = document.documentElement;
  if (!value) {
    root.style.removeProperty('--accent');
    return;
  }
  const hopLe =
    typeof CSS === 'undefined' || typeof CSS.supports !== 'function'
      ? /^[#a-zA-Z0-9(),.%/\s-]+$/.test(value)
      : CSS.supports('color', value);
  if (hopLe) root.style.setProperty('--accent', value);
  else root.style.removeProperty('--accent');
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [capabilities, setCapabilities] = useState<string[]>([]);
  const [mustSetupTwoFactor, setMust2fa] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [branding, setBranding] = useState<{ appName: string | null; tagline: string | null }>({
    appName: null,
    tagline: null,
  });
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  /**
   * Danh tính đang được cache phục vụ — OQ-305.
   *
   * `useRef` chứ không phải state: nó được đọc và ghi NGAY trong `applyMe`, còn
   * `user` thì phải chờ React vẽ lại. So với `user` ở đây sẽ so với giá trị của
   * lượt vẽ trước, và lượt đăng nhập đầu tiên sau khi tải trang sẽ so nhầm.
   */
  const chuCache = useRef<string | null>(null);

  const applyMe = useCallback(
    (me: MeResponse) => {
      /*
       * ── Cache phải theo NGƯỜI, không theo trình duyệt (OQ-305) ────────────
       *
       * **Chủ dự án**: *"đăng xuất từ tài khoản thành viên không có tên miền
       * nào xong đăng nhập lại tài khoản admin thì bị cache, phải F5 mới lại
       * load lại"*.
       *
       * React Query đánh khoá cache theo `queryKey` — `['domains']`,
       * `['dashboard']` — và không khoá nào mang danh tính người dùng. Nên hai
       * phiên khác nhau trên cùng một tab dùng chung một ngăn cache.
       *
       * Chủ dự án gặp chiều vô hại: thành viên 0 tên miền → admin vẫn thấy 0.
       * Chiều ngược lại mới là chuyện thật sự: **admin có 78 tên miền, thành
       * viên đăng nhập sau sẽ thấy đủ 78 cái** — kèm tên, kèm trạng thái — cho
       * tới khi họ tình cờ bấm F5. `visibleDomains()` canh gác đúng ở máy chủ,
       * nhưng dữ liệu đã nằm sẵn trong bộ nhớ trình duyệt từ phiên trước.
       *
       * Xoá theo ĐỔI DANH TÍNH chứ không chỉ lúc `logout()`: đăng xuất có thể
       * không chạy hết (đóng tab, mất mạng, phiên hết hạn ở tab khác), còn chỗ
       * này thì mọi đường vào đều đi qua.
       */
      if (chuCache.current !== null && chuCache.current !== me.user.id) {
        queryClient.clear();
      }
      chuCache.current = me.user.id;

      setUser(me.user);
      setCapabilities(me.capabilities ?? []);
      setMust2fa(Boolean(me.mustSetupTwoFactor));
      setMustChangePassword(Boolean(me.mustChangePassword));
      setBranding({ appName: me.branding?.appName ?? null, tagline: me.branding?.tagline ?? null });
      apDungMauNhan(me.theme);
    },
    [queryClient],
  );

  const refresh = useCallback(async () => {
    try {
      const me = await api.get<MeResponse>('/auth/me');
      applyMe(me);
    } catch (err) {
      // 401 là trạng thái hợp lệ: chưa đăng nhập.
      // 403 khi bị buộc bật 2FA — vẫn cần biết là đã đăng nhập.
      if (err instanceof ApiError && err.status === 403) {
        setMust2fa(true);
      } else {
        setUser(null);
        setCapabilities([]);
        setMust2fa(false);
      }
    } finally {
      setLoading(false);
    }
  }, [applyMe]);

  useEffect(() => {
    void ensureCsrfCookie().then(refresh);
  }, [refresh]);

  /*
   * Phiên chết giữa lúc đang dùng: `api.ts` phát sự kiện khi bất kỳ lời gọi
   * nghiệp vụ nào trả `401`. Xoá `user` ở đây là đủ — `RequireAuth` thấy
   * `user === null` sẽ tự `<Navigate to="/login">`, nên mọi liên kết trong app
   * đều về màn đăng nhập thay vì hiện `ErrorState` với nút "Thử lại" vô vọng.
   *
   * KHÔNG gọi `/auth/logout`: phiên đã chết rồi, lời gọi ấy cũng `401` và chỉ
   * thêm một vòng nữa.
   */
  useEffect(() => {
    const xuLy = (): void => {
      setUser(null);
      setCapabilities([]);
      setMust2fa(false);
      setLoading(false);
      /* Phiên chết cũng là một lượt đổi chủ — dọn cache như khi đăng xuất. */
      chuCache.current = null;
      queryClient.clear();
    };
    window.addEventListener(SU_KIEN_PHIEN_HET, xuLy);
    return () => window.removeEventListener(SU_KIEN_PHIEN_HET, xuLy);
  }, [queryClient]);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      setUser(null);
      setCapabilities([]);
      setMust2fa(false);
      /*
       * Dọn NGAY, không đợi người kế tiếp đăng nhập — OQ-305.
       *
       * Màn hình đăng nhập không được giữ dữ liệu của người vừa rời đi trong bộ
       * nhớ: máy có thể đang ở chỗ đông người, và một lượt "quay lại" của trình
       * duyệt là đủ để vẽ lại từ cache.
       */
      chuCache.current = null;
      queryClient.clear();
    }
  }, [queryClient]);

  const can = useCallback(
    (capability: string) => capabilities.includes(capability),
    [capabilities],
  );

  const value = useMemo<AuthValue>(
    () => ({
      user,
      capabilities,
      branding,
      mustSetupTwoFactor,
      mustChangePassword,
      loading,
      can,
      refresh,
      setSession: applyMe,
      logout,
    }),
    [
      user,
      capabilities,
      branding,
      mustSetupTwoFactor,
      mustChangePassword,
      loading,
      can,
      refresh,
      applyMe,
      logout,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth phải nằm trong <AuthProvider>');
  return ctx;
}
